import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdaptiveScale } from '../../hooks/useAdaptiveScale';
import PageHeader from '../layout/PageHeader';
import { getWarningStandard } from '../../api';
import { EarlyWarningStandard } from '../../api/types';
import styles from '../../styles/components/pages/warning-standard-detail.module.css';

const WarningStandardDetail: React.FC = () => {
  const { scaleFactor, deviceType } = useAdaptiveScale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [standard, setStandard] = useState<EarlyWarningStandard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 获取URL参数
  const deptId = searchParams.get('deptId');
  const identity = searchParams.get('identity');
  const module = searchParams.get('module');
  const deptName = searchParams.get('deptName');

  // 根据identity和module计算预警标准ID
  const getStandardId = React.useCallback((): number => {
    if (identity === 'school') {
      switch (module) {
        case 'budget': return 1;
        case 'nutrition': return 2;
        case 'safety': return 3;
        default: return 1;
      }
    } else if (identity === 'supervision') {
      switch (module) {
        case 'budget': return 4;
        case 'nutrition': return 5;
        case 'safety': return 6;
        default: return 4;
      }
    }
    return 1; // 默认值
  }, [identity, module]);

  // 获取模块名称
  const getModuleName = (): string => {
    switch (module) {
      case 'budget': return '膳食经费';
      case 'nutrition': return '营养配餐';
      case 'safety': return '食品安全';
      default: return '膳食经费';
    }
  };

  // 加载预警标准数据
  useEffect(() => {
    const loadStandard = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const standardId = getStandardId();
        const data = await getWarningStandard(standardId);
        console.log('预警标准数据:', data);
        
        setStandard(data.data);
      } catch (err) {
        console.error('获取预警标准失败:', err);
        setError(err instanceof Error ? err.message : 'Failed to load standard');
      } finally {
        setLoading(false);
      }
    };

    loadStandard();
  }, [getStandardId]);

  // 返回主页面
  const handleBack = () => {
    const params = new URLSearchParams();
    if (deptId) params.set('deptId', deptId);
    if (identity) params.set('identity', identity);
    if (deptName) params.set('deptName', deptName);
    navigate(`/?${params.toString()}`);
  };

  return (
    <div 
      className={`${styles.detailContainer} responsive-container scale-viewport`}
      style={{ 
        '--scale-factor': scaleFactor,
        '--device-type': deviceType 
      } as React.CSSProperties}
    >
      <PageHeader />
      
      <main className={styles.mainContent}>
        <div className={styles.contentCard}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              ← 返回
            </button>
            <h1 className={styles.title}>
              {getModuleName()}预警标准
            </h1>
          </div>
          
          <div className={styles.content}>
            {loading && (
              <div className={styles.loading}>加载中...</div>
            )}
            
            {error && (
              <div className={styles.error}>
                加载失败: {error}
              </div>
            )}
            
            {standard && (
              standard.content && standard.content.trim() ? (
                <div 
                  className={styles.richContent}
                  dangerouslySetInnerHTML={{ __html: standard.content }}
                />
              ) : (
                <div className={styles.empty}>
                  暂无预警标准内容
                </div>
              )
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default WarningStandardDetail;
